'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  serviceType: string;
  urgency: string;
  name: string;
  organization: string;
  email: string;
  phone: string;
  location: string;
  subject: string;
  message: string;
  preferredContact: string;
}

export default function ContactFormSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    serviceType: '',
    urgency: '',
    name: '',
    organization: '',
    email: '',
    phone: '',
    location: '',
    subject: '',
    message: '',
    preferredContact: 'email',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const serviceTypes = [
    'Telecom Infrastructure',
    'Electrical Systems',
    'AMC Services',
    'Emergency Repair',
    'Project Consultation',
    'Partnership Inquiry',
    'General Inquiry',
  ];

  const urgencyLevels = [
    { value: 'emergency', label: 'Emergency (Immediate)', color: 'text-destructive' },
    { value: 'high', label: 'High (Within 24 hours)', color: 'text-warning' },
    { value: 'medium', label: 'Medium (2-3 days)', color: 'text-accent' },
    { value: 'low', label: 'Low (Within a week)', color: 'text-success' },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitStatus('success');

    // Reset form after success
    setTimeout(() => {
      setFormData({
        serviceType: '',
        urgency: '',
        name: '',
        organization: '',
        email: '',
        phone: '',
        location: '',
        subject: '',
        message: '',
        preferredContact: 'email',
      });
      setSubmitStatus('idle');
    }, 3000);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted">
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-lg p-8 border border-border">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-3 pt-6">
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-textPrimary mb-4">
            Service Request Form
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            Submit your service request and our team will respond based on the urgency level selected.
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 border border-border shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Service Type and Urgency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="serviceType" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Service Type <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
                  >
                    <option value="">Select service type</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="urgency" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Urgency Level <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
                  >
                    <option value="">Select urgency level</option>
                    {urgencyLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none"
                  />
                </div>
              </div>
            </div>

            {/* Name and Organization */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Full Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Organization <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Company or organization name"
                />
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Phone Number <span className="text-destructive">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Location <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="City, State"
              />
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Subject <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Brief description of your request"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Detailed Message <span className="text-destructive">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Provide detailed information about your service request, including any specific requirements or concerns..."
              ></textarea>
            </div>

            {/* Preferred Contact Method */}
            <div>
              <label className="block text-sm font-headline font-bold text-textPrimary mb-3">
                Preferred Contact Method
              </label>
              <div className="flex flex-wrap gap-4">
                {['email', 'phone', 'both'].map((method) => (
                  <label key={method} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferredContact"
                      value={method}
                      checked={formData.preferredContact === method}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-sm font-body text-textPrimary capitalize">{method}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-lg font-headline font-bold text-lg shadow-lg hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    Submit Service Request
                  </>
                )}
              </button>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0" />
                <div>
                  <h4 className="font-headline font-bold text-success mb-1">Request Submitted Successfully!</h4>
                  <p className="text-sm text-textSecondary">
                    Our team will review your request and respond within the timeframe specified for your urgency level.
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Additional Information */}
        <div className="mt-8 bg-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-lg font-headline font-bold text-textPrimary mb-2">Response Time Guidelines</h4>
              <ul className="space-y-2 text-sm text-textSecondary">
                <li className="flex items-start gap-2">
                  <Icon name="ClockIcon" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                  <span><strong>Emergency:</strong> Immediate response, team dispatched within 30 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="ClockIcon" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                  <span><strong>High Priority:</strong> Response within 4 hours, resolution within 24 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="ClockIcon" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                  <span><strong>Medium Priority:</strong> Response within 24 hours, resolution within 2-3 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="ClockIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span><strong>Low Priority:</strong> Response within 48 hours, resolution within a week</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}